/**
 * Superadmin — Doctor Cotations Manager
 * Per user spec: "as super admin can i update /assigne remove cotation for any dr etc"
 * Add/remove NGAP acts per doctor, modify coefficients, set base prices, restrict.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings, User, Plus, X, Save, AlertCircle, Stethoscope, Calculator, Shield, CheckCircle2 } from "lucide-react";
import { MOCK_DOCTORS, MOCK_COTATIONS, getActsForDoctor, SPECIALTY_DEFAULT_ACTS, SPECIALTY_LABEL, type DoctorCotation } from "@/lib/referential/doctor-cotations";
import { NGAP_ACTS, CATEGORY_LABEL, CATEGORY_TONE, type NGAPAct } from "@/lib/referential/ngap";

export const Route = createFileRoute("/admin/doctor-cotations")({
  component: DoctorCotationsManager,
});

function DoctorCotationsManager() {
  const [selectedDoctorId, setSelectedDoctorId] = useState(MOCK_DOCTORS[0].id);
  const [cotations, setCotations] = useState<DoctorCotation[]>(MOCK_COTATIONS);
  const [search, setSearch] = useState("");

  const selectedCotation = cotations.find((c) => c.doctorId === selectedDoctorId)!;
  const selectedDoctor = MOCK_DOCTORS.find((d) => d.id === selectedDoctorId)!;

  const allowedCodes = new Set([
    ...selectedCotation.defaultAllowedActs,
    ...selectedCotation.customAssignments.map((a) => a.code),
  ]);
  const restrictedCodes = new Set(selectedCotation.restricted);

  function toggleAct(code: string, isAssigned: boolean) {
    setCotations((cs) => cs.map((c) => {
      if (c.doctorId !== selectedDoctorId) return c;
      if (isAssigned) {
        // Remove
        return {
          ...c,
          defaultAllowedActs: c.defaultAllowedActs.filter((x) => x !== code),
          customAssignments: c.customAssignments.filter((x) => x.code !== code),
          restricted: [...c.restricted, code],
        };
      } else {
        // Add
        return {
          ...c,
          defaultAllowedActs: c.defaultAllowedActs.includes(code) ? c.defaultAllowedActs : [...c.defaultAllowedActs, code],
          customAssignments: c.customAssignments.filter((x) => x.code === code),
          restricted: c.restricted.filter((x) => x !== code),
        };
      }
    }));
  }

  function updateCoefficient(code: string, newCoeff: number) {
    setCotations((cs) => cs.map((c) => {
      if (c.doctorId !== selectedDoctorId) return c;
      const existing = c.customAssignments.find((a) => a.code === code);
      if (existing) {
        return { ...c, customAssignments: c.customAssignments.map((a) => a.code === code ? { ...a, customCoefficient: newCoeff } : a) };
      }
      return { ...c, customAssignments: [...c.customAssignments, { code, customCoefficient: newCoeff, note: "Override superadmin" }] };
    }));
  }

  function setAsSuperadminAssignment(code: string) {
    setCotations((cs) => cs.map((c) => {
      if (c.doctorId !== selectedDoctorId) return c;
      const customExists = c.customAssignments.find((a) => a.code === code);
      if (customExists) {
        return { ...c, customAssignments: c.customAssignments.filter((a) => a.code !== code) };
      }
      return { ...c, customAssignments: [...c.customAssignments, { code, note: "Ajouté par superadmin" }] };
    }));
  }

  const filteredActs = NGAP_ACTS.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return a.code.toLowerCase().includes(s) || a.label.toLowerCase().includes(s) || a.category.includes(s);
  });

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="text-[10px]">SUPERADMIN</Badge>
        <span className="text-sm text-muted-foreground">· Accès restreint · Audit WORM</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Settings className="h-5 w-5" /> Cotations médecins
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gérer les actes NGAP autorisés par médecin · Modifier les coefficients · Restreindre l'accès
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-[300px_1fr]">
        {/* Doctor list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Médecins ({MOCK_DOCTORS.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 p-2">
            {MOCK_DOCTORS.map((d) => {
              const cot = cotations.find((c) => c.doctorId === d.id);
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDoctorId(d.id)}
                  className={`w-full text-left rounded-md p-2 text-sm transition ${
                    selectedDoctorId === d.id ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-white/5"
                  }`}
                >
                  <div className="font-medium">{d.name}</div>
                  <div className="text-[11px] text-muted-foreground">{SPECIALTY_LABEL[d.specialty]}</div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Badge variant="outline" className="text-[9px] h-4">{cot?.defaultAllowedActs.length} actes</Badge>
                    {cot?.customAssignments.length ? <Badge variant="secondary" className="text-[9px] h-4">+{cot.customAssignments.length} custom</Badge> : null}
                    {cot?.restricted.length ? <Badge variant="destructive" className="text-[9px] h-4">-{cot.restricted.length}</Badge> : null}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Detail */}
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-4 w-4" /> {selectedDoctor.name}
                  </CardTitle>
                  <CardDescription>
                    <code className="text-xs">{selectedDoctor.id}</code> · INPE {selectedDoctor.inpe} · {SPECIALTY_LABEL[selectedDoctor.specialty]}
                  </CardDescription>
                </div>
                <Button size="sm"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all">
                <TabsList>
                  <TabsTrigger value="all"><Calculator className="h-3.5 w-3.5 mr-1" /> Tous les actes ({NGAP_ACTS.length})</TabsTrigger>
                  <TabsTrigger value="assigned"><CheckCircle2Alt /> Assignés ({allowedCodes.size})</TabsTrigger>
                  <TabsTrigger value="custom">Custom ({selectedCotation.customAssignments.length})</TabsTrigger>
                  <TabsTrigger value="restricted">Restreints ({selectedCotation.restricted.length})</TabsTrigger>
                </TabsList>
                <div className="mt-3">
                  <Input
                    placeholder="Rechercher (code, libellé, catégorie)..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <TabsContent value="all" className="mt-3">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filteredActs.map((a) => {
                      const isAssigned = allowedCodes.has(a.code);
                      const isRestricted = restrictedCodes.has(a.code);
                      const isDefault = selectedCotation.defaultAllowedActs.includes(a.code);
                      return (
                        <ActRow
                          key={a.code}
                          act={a}
                          isAssigned={isAssigned}
                          isDefault={isDefault}
                          isRestricted={isRestricted}
                          onToggle={() => toggleAct(a.code, isAssigned)}
                          onSetCustom={() => setAsSuperadminAssignment(a.code)}
                          onCoeffChange={(c) => updateCoefficient(a.code, c)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="assigned" className="mt-3">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filteredActs.filter((a) => allowedCodes.has(a.code)).map((a) => {
                      const isDefault = selectedCotation.defaultAllowedActs.includes(a.code);
                      return (
                        <ActRow
                          key={a.code}
                          act={a}
                          isAssigned={true}
                          isDefault={isDefault}
                          isRestricted={false}
                          onToggle={() => toggleAct(a.code, true)}
                          onSetCustom={() => setAsSuperadminAssignment(a.code)}
                          onCoeffChange={(c) => updateCoefficient(a.code, c)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="custom" className="mt-3">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {selectedCotation.customAssignments.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
                        Aucune assignation custom · Allez dans "Tous les actes" pour en ajouter
                      </div>
                    ) : (
                      selectedCotation.customAssignments.map((ca) => {
                        const act = NGAP_ACTS.find((a) => a.code === ca.code);
                        if (!act) return null;
                        return (
                          <ActRow
                            key={act.code}
                            act={act}
                            isAssigned={true}
                            isDefault={false}
                            isRestricted={false}
                            onToggle={() => toggleAct(act.code, true)}
                            onSetCustom={() => setAsSuperadminAssignment(act.code)}
                            onCoeffChange={(c) => updateCoefficient(act.code, c)}
                            customNote={ca.note}
                            customCoeff={ca.customCoefficient}
                          />
                        );
                      })
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="restricted" className="mt-3">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {selectedCotation.restricted.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
                        Aucun acte restreint
                      </div>
                    ) : (
                      selectedCotation.restricted.map((code) => {
                        const act = NGAP_ACTS.find((a) => a.code === code);
                        if (!act) return null;
                        return (
                          <ActRow
                            key={act.code}
                            act={act}
                            isAssigned={false}
                            isDefault={false}
                            isRestricted={true}
                            onToggle={() => toggleAct(act.code, false)}
                            onSetCustom={() => setAsSuperadminAssignment(act.code)}
                            onCoeffChange={(c) => updateCoefficient(act.code, c)}
                          />
                        );
                      })
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ActRow({
  act, isAssigned, isDefault, isRestricted, onToggle, onSetCustom, onCoeffChange, customNote, customCoeff,
}: {
  act: NGAPAct; isAssigned: boolean; isDefault: boolean; isRestricted: boolean;
  onToggle: () => void; onSetCustom: () => void; onCoeffChange: (c: number) => void;
  customNote?: string; customCoeff?: number;
}) {
  return (
    <div className={`flex items-center gap-3 rounded-md border p-2 ${isAssigned ? "border-emerald-500/20 bg-emerald-500/5" : isRestricted ? "border-red-500/20 bg-red-500/5" : "border-white/5 bg-white/[0.02]"}`}>
      <Checkbox checked={isAssigned} onCheckedChange={onToggle} />
      <div className="grid h-7 w-7 place-items-center rounded bg-primary/10 text-primary font-mono text-[10px] font-bold">
        {act.lettrage}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <code className="font-mono font-bold text-xs">{act.code}</code>
          <span className="font-medium text-sm truncate">{act.label}</span>
          <Badge variant={CATEGORY_TONE[act.category]} className="text-[9px]">{CATEGORY_LABEL[act.category]}</Badge>
          {isDefault && <Badge variant="secondary" className="text-[9px]">défaut</Badge>}
          {isRestricted && <Badge variant="destructive" className="text-[9px]">restreint</Badge>}
          {customNote && <Badge variant="outline" className="text-[9px]"><Shield className="h-2.5 w-2.5 mr-0.5" />{customNote}</Badge>}
        </div>
        <div className="text-[11px] text-muted-foreground">{act.description}</div>
      </div>
      <div className="text-right text-[10px] text-muted-foreground shrink-0">
        <div>Coef: <span className="font-mono">{customCoeff ?? act.coefficient}</span></div>
        <div>Base: <span className="font-mono">{(act.basePriceCents / 100).toFixed(2)} MAD</span></div>
        <div>Total: <span className="font-mono">{(act.basePriceCents * (customCoeff ?? act.coefficient) / 100).toFixed(2)} MAD</span></div>
      </div>
      {isAssigned && (
        <div className="flex items-center gap-1">
          <Input type="number" className="w-16 h-7 text-xs" placeholder="Coef" defaultValue={customCoeff ?? act.coefficient} onBlur={(e) => onCoeffChange(Number(e.target.value))} />
          <Button size="sm" variant="ghost" onClick={onSetCustom} title={customNote ? "Retirer le statut custom" : "Marquer comme custom (superadmin override)"}>
            {customNote ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          </Button>
        </div>
      )}
    </div>
  );
}

function CheckCircle2Alt() {
  return <CheckCircle2 className="h-3.5 w-3.5 mr-1" />;
}
